import type { ReactNode } from 'react';

import { Numeral } from '@ui/uikit/components/common/Numeral';
import { PriceDetailItem } from '@ui/uikit/components/common/PriceDetails/PriceDetailItem';

import type {
  DetailsProps,
  IsSqrvVotingPowerUsed,
  IsValidationFinished,
} from '../ApplicationValidationSummary';
import type { ChoiceConfig } from '../constants';
import { CHOICES_BY_ID } from '../constants';
import styles from './ApplicationValidationDetails.module.scss';
import { getValidationCountdown } from './getValidationCountdown';

type ApplicationValidationDetailsProps = DetailsProps &
  IsValidationFinished &
  IsSqrvVotingPowerUsed;
type ChoiceDetails = ChoiceConfig & {
  id: string;
  count: ReactNode;
  percent: number;
};

const tooltipMessage = `This is Total Voting Power (TVP), expressed in SQRv points, calculated as TVP = BVP + TSVP. BVP is the number of SQR tokens user holds, and TSVP depends on how many SQR tokens user stakes and the duration of the stake (Not real-time, 1-hour delay).`;

const renderVotingCount = (value: number, isSQRvMode?: boolean) => (
  <>
    <Numeral format="compact" value={value} />
    {isSQRvMode && ' SQRv'}
  </>
);

export const ApplicationValidationDetails = ({
  voteStats,
  validationPeriod,
  isSqrvVotingPowerUsed = false,
  isValidationFinished = false,
  preSnapshotSqrvVotingPower = 0,
}: ApplicationValidationDetailsProps) => {
  const approvedPercentage = voteStats?.approvedPercentage ?? 0;
  const rejectedPercentage = voteStats?.rejectedPercentage ?? 0;
  const totalVotingPower = voteStats?.totalVotes ?? 0;

  const approvedCount = Math.round((totalVotingPower * approvedPercentage) / 100);
  const rejectedCount = totalVotingPower - approvedCount;

  const CHOICES: ChoiceDetails[] = [
    {
      ...CHOICES_BY_ID.approved,
      id: 'approved',
      count: renderVotingCount(approvedCount, isSqrvVotingPowerUsed),
      percent: approvedPercentage,
    },
    {
      ...CHOICES_BY_ID.rejected,
      id: 'rejected',
      count: renderVotingCount(rejectedCount, isSqrvVotingPowerUsed),
      percent: rejectedPercentage,
    },
  ];

  const countdown = getValidationCountdown({
    startDate: validationPeriod?.startDate,
    endDate: validationPeriod?.endDate,
    formattedEndDate: validationPeriod?.formattedEndDate,
  });

  return (
    <div className={styles.container}>
      {isSqrvVotingPowerUsed && !isValidationFinished ? (
        <PriceDetailItem
          key="sqrv-voting-power"
          description="Validation Power"
          value={
            <>
              <Numeral format="compact" value={preSnapshotSqrvVotingPower} /> SQRv
            </>
          }
          tooltip={tooltipMessage}
        />
      ) : (
        CHOICES.map(({ id, iconColor, icon: Icon, label, count, percent }) => (
          <PriceDetailItem
            key={id}
            description={
              <div className={styles.description}>
                <Icon color={iconColor} height={16} width={16} /> {label}
              </div>
            }
            value={
              <>
                {count} / {`${percent}%`}
              </>
            }
          />
        ))
      )}
      {countdown && <PriceDetailItem description={countdown.description} value={countdown.value} />}
    </div>
  );
};
